import { useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  UsersIcon as PeopleIcon,
  BookOpenIcon,
  BuildingIcon,
  ListIcon,
  BarChart3Icon,
  DollarSignIcon,
  FlaskConicalIcon,
  MessageSquareIcon,
  SparklesIcon,
  ActivityIcon,
  GaugeIcon,
  CoinsIcon,
  ChartColumnIcon,
  FileTextIcon,
  SlidersHorizontalIcon,
  ChartNoAxesCombinedIcon,
  LayoutDashboardIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from 'lucide-react';
import type { MenuItem } from '@/lib/types';

const menuItems: MenuItem[] = [
  {
    label: '顧客一覧',
    icon: PeopleIcon,
    href: '/',
  },
  {
    label: 'ドキュメント',
    icon: BookOpenIcon,
    href: '/docs',
  },
  {
    label: 'mise',
    icon: BuildingIcon,
    children: [
      {
        label: '企業一覧',
        icon: ListIcon,
        href: '/mise/companies',
      },
      {
        label: '利用状況',
        icon: BarChart3Icon,
        href: '/mise/usage',
      },
      {
        label: 'LLMコスト',
        icon: DollarSignIcon,
        href: '/mise/llm-cost',
      },
      {
        label: 'お問い合わせ管理',
        icon: MessageSquareIcon,
        href: '/mise/inquiries',
      },
    ],
  },
  {
    label: 'DS',
    icon: FlaskConicalIcon,
    children: [
      {
        label: '予測誤差評価',
        icon: BarChart3Icon,
        href: '/ds/prediction-error-evaluation',
      },
    ],
  },
  {
    label: 'ダッシュボード',
    icon: LayoutDashboardIcon,
    children: [
      {
        label: 'カスタムダッシュボード',
        icon: ChartNoAxesCombinedIcon,
        href: '/custom-dashboard',
      },
      {
        label: 'LLMコスト',
        icon: DollarSignIcon,
        href: '/dashboard/llm-cost',
      },
    ],
  },
  {
    label: 'AI',
    icon: SparklesIcon,
    children: [
      {
        label: 'MCP利用状況',
        icon: ActivityIcon,
        href: '/ai/mcp-usage',
      },
    ],
  },
  {
    label: '使用量集計',
    icon: GaugeIcon,
    href: '/usage-aggregation',
  },
  {
    label: 'クレジット制',
    icon: CoinsIcon,
    children: [
      {
        label: '消費一覧',
        icon: ChartColumnIcon,
        href: '/credit/consumption',
      },
      {
        label: '契約管理',
        icon: FileTextIcon,
        href: '/credit/contracts',
      },
      {
        label: '換算設定',
        icon: SlidersHorizontalIcon,
        href: '/credit/conversion-settings',
      },
    ],
  },
];

/** サブメニューを持つ項目のprefixを自動検出し、初期展開状態を決定する */
function getInitialOpenMenus(pathname: string, items: MenuItem[]): Record<string, boolean> {
  const openMenus: Record<string, boolean> = {};
  for (const item of items) {
    if (item.children) {
      const isChildActive = item.children.some(
        (child) => child.href && pathname.startsWith(child.href),
      );
      openMenus[item.label] = isChildActive;
    }
  }
  return openMenus;
}

export function Sidebar() {
  const { pathname } = useLocation();

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() =>
    getInitialOpenMenus(pathname, menuItems),
  );

  const toggleMenu = (label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isSelected = (href: string) => {
    if (href === '/') {
      return pathname === '/' || pathname.startsWith('/customers');
    }
    return pathname.startsWith(href);
  };

  return (
    <Box sx={{ pt: 9 }}>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography
          variant="overline"
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            color: '#94a3b8',
            letterSpacing: '0.1em',
          }}
        >
          ナビゲーション
        </Typography>
      </Box>
      <List sx={{ px: 1 }}>
        {menuItems.map((item) => {
          const IconComponent = item.icon;

          // サブメニューを持つ項目
          if (item.children) {
            const isChildSelected = item.children.some(
              (child) => child.href && isSelected(child.href),
            );
            const isOpen = openMenus[item.label] ?? false;

            return (
              <Box key={item.label}>
                <ListItemButton
                  onClick={() => toggleMenu(item.label)}
                  selected={isChildSelected && !isOpen}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon>
                    <IconComponent size={18} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                  />
                  {isOpen ? (
                    <ChevronDownIcon size={16} style={{ color: '#94a3b8' }} />
                  ) : (
                    <ChevronRightIcon size={16} style={{ color: '#94a3b8' }} />
                  )}
                </ListItemButton>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <ListItemButton
                          key={child.href}
                          selected={isSelected(child.href || '')}
                          component={Link}
                          to={child.href || '/'}
                          sx={{ pl: 4, py: 0.75 }}
                        >
                          <ListItemIcon>
                            <ChildIcon size={16} />
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{ fontSize: '0.825rem' }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          // 通常のメニュー項目
          return (
            <ListItemButton
              key={item.href}
              selected={isSelected(item.href || '')}
              component={Link}
              to={item.href || '/'}
              sx={{ py: 1 }}
            >
              <ListItemIcon>
                <IconComponent size={18} />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}
